import React from 'react';
import { useLocation } from 'react-router-dom';
import { formatISO } from 'date-fns/esm';
import { useAppDispatch, useAppSelector } from '../../redux/store';
import { getSmbLocations } from '../../companySettings/redux/CompanySettingsActions';
import { selectTaxesState } from '../../companySettings/redux/CompanySettingsSelectors';
import MenuBuilderService from '../../menus/services/MenuBuilderService';
import { TablesService } from '../../companyLocations/services/tablesService';
import { TaxService } from '../../companySettings/services/TaxService';
import now from '../../utils/dateUtils/now';
import OnboardingChecklist, { OnboardingChecklistItem } from './OnboardingChecklist';

interface ChecklistCompletion {
  loaded: boolean;
  hasMenu: boolean;
  hasTaxes: boolean;
  hasTableCode: boolean;
}

// Rendered once inside AppLayout (above the router outlet), so it shows on whatever page a
// self-serve company lands on — not just a first visit to Home — and only fetches its data
// once per session rather than on every page navigation.
const OnboardingChecklistContainer = () => {
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();
  const contextSession = useAppSelector((state) => state.session.contextSession);
  const contextId = contextSession.id;
  const isCompanyContext = contextSession.type === 'company';

  const { smbConfiguredLocations } = useAppSelector(selectTaxesState);
  const primaryLocationId = smbConfiguredLocations[0]?._id;

  // Every company user hits this, including ones without company.view - a failure here must stay
  // silent, or the checklist's own probe would knock them onto the Access Denied page.
  React.useEffect(() => {
    if (isCompanyContext && contextId) {
      dispatch(getSmbLocations(contextId, { raisePageError: false }));
    }
  }, [dispatch, contextId, isCompanyContext]);

  const isSmbCompany = isCompanyContext && !!primaryLocationId;

  const dismissKey = contextId ? `onboardingChecklistDismissed_${contextId}` : '';
  const [dismissed, setDismissed] = React.useState(false);
  React.useEffect(() => {
    if (!dismissKey) return;
    try {
      setDismissed(window.localStorage.getItem(dismissKey) === 'true');
    } catch {
      setDismissed(false);
    }
  }, [dismissKey]);

  // Open by default the first time a company sees this (so the tab doesn't go unnoticed),
  // then remember whatever the user last left it as.
  const openKey = contextId ? `onboardingChecklistOpen_${contextId}` : '';
  const [open, setOpen] = React.useState(true);
  React.useEffect(() => {
    if (!openKey) return;
    try {
      const stored = window.localStorage.getItem(openKey);
      setOpen(stored === null ? true : stored === 'true');
    } catch {
      setOpen(true);
    }
  }, [openKey]);

  const [completion, setCompletion] = React.useState<ChecklistCompletion>({
    loaded: false,
    hasMenu: false,
    hasTaxes: false,
    hasTableCode: false,
  });

  // Re-runs on every navigation (not just once on mount) so completing a step on one page — e.g.
  // creating a table — is picked up as soon as the checklist is next consulted, the same way the
  // "orders" step below reacts to navigation rather than staying frozen at its initial value.
  React.useEffect(() => {
    if (!isSmbCompany || !primaryLocationId) return;
    let cancelled = false;
    const load = async () => {
      try {
        const [menus, taxes, tableConfigurations] = await Promise.all([
          MenuBuilderService.getMenus(contextId, primaryLocationId),
          TaxService.getTaxesForCompany(contextId),
          TablesService.getTableConfigurations(contextId, primaryLocationId, '', 1),
        ]);
        // IPagedResponse's declared `results` field doesn't match the API's actual `items` field.
        const tables: any[] = (tableConfigurations as any).items ?? (tableConfigurations as any).results ?? [];
        if (!cancelled) {
          setCompletion({
            loaded: true,
            hasMenu: menus.length > 0,
            hasTaxes: taxes.length > 0,
            hasTableCode: tables.some((table) => !!table.targetCode),
          });
        }
      } catch {
        if (!cancelled) setCompletion((prev) => ({ ...prev, loaded: true }));
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [isSmbCompany, contextId, primaryLocationId, pathname, open]);

  // No data signal exists for "has the merchant looked at incoming orders," so this step is
  // marked complete the first time they actually navigate to their location's orders page.
  const viewedOrdersKey = contextId ? `onboardingChecklistViewedOrders_${contextId}` : '';
  const [hasViewedOrders, setHasViewedOrders] = React.useState(false);
  React.useEffect(() => {
    if (!viewedOrdersKey) return;
    try {
      setHasViewedOrders(window.localStorage.getItem(viewedOrdersKey) === 'true');
    } catch {
      setHasViewedOrders(false);
    }
  }, [viewedOrdersKey]);

  React.useEffect(() => {
    if (hasViewedOrders || !contextId || !primaryLocationId) return;
    const ordersPathPrefix = `/companies/${contextId}/locations/${primaryLocationId}/orders`;
    if (pathname.startsWith(ordersPathPrefix)) {
      setHasViewedOrders(true);
      if (viewedOrdersKey) {
        try {
          window.localStorage.setItem(viewedOrdersKey, 'true');
        } catch {
          // ignore write failures (e.g. private browsing)
        }
      }
    }
  }, [pathname, contextId, primaryLocationId, hasViewedOrders, viewedOrdersKey]);

  const allComplete =
    completion.loaded && completion.hasMenu && completion.hasTaxes && completion.hasTableCode && hasViewedOrders;
  const showChecklist = isSmbCompany && completion.loaded && !dismissed && !allComplete;

  if (!showChecklist) {
    return null;
  }

  const handleDismiss = () => {
    setDismissed(true);
    if (dismissKey) {
      try {
        window.localStorage.setItem(dismissKey, 'true');
      } catch {
        // ignore write failures (e.g. private browsing)
      }
    }
  };

  const handleToggleOpen = () => {
    const next = !open;
    setOpen(next);
    if (openKey) {
      try {
        window.localStorage.setItem(openKey, String(next));
      } catch {
        // ignore write failures (e.g. private browsing)
      }
    }
  };

  // If the company has more than one basicpos location, send them to a location picker rather
  // than guessing; with just one, skip straight to that location.
  const hasSingleSmbLocation = smbConfiguredLocations.length === 1;
  const tablesLinkTo = hasSingleSmbLocation
    ? `/companies/${contextId}/companyLocations/${primaryLocationId}/tables/`
    : `/companies/${contextId}/companyLocations`;
  const menuLinkTo = hasSingleSmbLocation
    ? `/companies/${contextId}/locations/${primaryLocationId}/menu-builder/menus`
    : `/companies/${contextId}/menu-builder`;

  const items: OnboardingChecklistItem[] = [
    {
      key: 'tables',
      label: 'Add your table codes',
      complete: completion.hasTableCode,
      instructions:
        'Go to your location\'s Tables page and click "+ New Table." Give it a name — we\'ll automatically generate a unique QR code for it. Click the code next to any table to view, download, or print it.',
      linkTo: tablesLinkTo,
      linkLabel: 'Go to Tables',
    },
    {
      key: 'taxes',
      label: 'Set up your taxes',
      complete: completion.hasTaxes,
      instructions:
        'Go to Company Settings and click "+ New Tax." Give it a name (like "HST") and a percentage rate. Once it\'s created, you can apply it to any menu item.',
      linkTo: `/companies/${contextId}/companySettings`,
      linkLabel: 'Go to Company Settings',
    },
    {
      key: 'menu',
      label: 'Create your first menu',
      complete: completion.hasMenu,
      instructions:
        'Go to Menus and click "+ New Menu" to create a category, like "Appetizers" or "Drinks." Then add items to it — each one needs a name, price, and description.',
      linkTo: menuLinkTo,
      linkLabel: 'Go to Menus',
    },
    {
      key: 'orders',
      label: 'See incoming orders',
      complete: hasViewedOrders,
      instructions:
        'Once your menu is live, orders placed by guests show up here in real time so you can start fulfilling them.',
      linkTo: `/companies/${contextId}/locations/${primaryLocationId}/orders/day/${formatISO(now(), {
        representation: 'date',
      })}/upcoming`,
      linkLabel: 'Go to Orders',
    },
  ];

  return <OnboardingChecklist items={items} open={open} onToggleOpen={handleToggleOpen} onDismiss={handleDismiss} />;
};

export default OnboardingChecklistContainer;
